// -----------------------------------------------------------------------
// <copyright file="ServiceRequestPageManager.tsx" company="Microsoft">
//     copyright (c) Microsoft. All rights reserved.
// </copyright>
// -----------------------------------------------------------------------

import { ITheme, mergeStyles } from '@fluentui/react';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { IAppState, ServiceRequestPage, ServiceRequestPageType } from '../../../web-legacy/models/reduxState';

import {
  getCurrentServiceRequestPageType,
  isAgaveSelector,
} from '../../../web-legacy/packages/app/selectors/contactUsSelector';
import AlchemyThemeRoutingContainer from '../../../web-legacy/packages/contactUs/containerComponents/AlchemyThemeRoutingContainer';
import { ModalityOptionsContainer } from '../../../web-legacy/packages/contactUs/containerComponents/ModalityOptionsContainer';
import { RequestConfirmationContainer } from '../../../web-legacy/packages/contactUs/containerComponents/RequestConfirmationContainer';
import { RequestCreationContainer } from '../../../web-legacy/packages/contactUs/containerComponents/RequestCreationContainer';
import { ExtensionAttributeKeys } from '../../../web-legacy/packages/contactUs/models/requestModels';
import { initializeToPage } from '../../../web-legacy/packages/contactUs/reduxUpdater/actions/modalitySectionActions';
import {
  getContactUsClassNames,
  IContactUsStyles,
} from '../../../web-legacy/packages/contactUs/styles/contactUsStyles';
import { IStyledComponent } from '../../../web-legacy/packages/contactUs/styles/themesUI';
import { getHelpLogger } from '../../utils/getHelpLogger';
import { PageHeader } from './PageHeader';
import { SignInSection } from './SignInSection';
import { collectTelemetry } from '../../utils/oneDsUtil';
import { LogHint, LogType } from '../../models/emeraldLoggingRequest';

interface IProps {
  theme?: ITheme;
}

type IServiceRequestPageManagerProps = IProps & IStyledComponent<IContactUsStyles>;

export const ServiceRequestPageManager = (props: IServiceRequestPageManagerProps): JSX.Element => {
  const isAgave = useSelector(isAgaveSelector);
  const contactUsClassNames = getContactUsClassNames(props.styles, { theme: props.theme, isAgave: isAgave });
  const modalityState = useSelector((appState: IAppState) => appState.ContactState.ModalitySectionState);
  const sharedState = useSelector((appState: IAppState) => appState.ContactState.SharedState);
  const isRenderingModalitySection = modalityState.isRenderingModalitySection;
  const isCollapsedModalities = modalityState.isCollapsedModalities;
  const pages = modalityState.serviceRequestPages;
  const currentPageType: ServiceRequestPageType = getCurrentServiceRequestPageType(modalityState);
  const dispatch = useDispatch();
  const dispatchInitializeToPage = useCallback(
    (page: ServiceRequestPage) => dispatch(initializeToPage(page)),
    [dispatch]
  );

  collectTelemetry({
    ContentId: 'ServiceRequestPageLoaded', Type: LogType.ContentUpdate, Hint: LogHint.ServiceRequestPageLoaded,
    Details: { 'pageLength': pages?.length, 'currentPageType': pages?.length > 0 ? ServiceRequestPageType[currentPageType] : '' }
  });
  if (!pages || pages.length <= 0) {
    getHelpLogger.logSeriveRequestManagerLoaded(null, null);
                
                                                  
             

    const skipCategoryDropDownPage =
      !!modalityState.draftRequest.ExtensionAttributes &&
      !!modalityState.draftRequest.ExtensionAttributes[ExtensionAttributeKeys.ThemeL1];

    if (modalityState.alchemyThemeRoutingEnabled && !modalityState.isOrgId) {
      if (skipCategoryDropDownPage) {
        const page: ServiceRequestPage = {
          isBackButtonPresent: true,
          serviceRequestPageType: ServiceRequestPageType.ModalityOptions, // Skip to modalityOptions page
        };
        dispatchInitializeToPage(page);
      } else {
        const page: ServiceRequestPage = {
          isBackButtonPresent: (sharedState.isWindowsCopilotRequestCreationDeeplink || sharedState.isCMCRequestCreationDeeplink) ? false : true,
          serviceRequestPageType: ServiceRequestPageType.AlchemyThemeRouting, // Proceed to AlchemyThemeRouting page
        };
        dispatchInitializeToPage(page);
      }
      // If alchemyThemeRoutingEnabled is false (For non-SMC) and second fetchUserInfo is successful, call fetchAvailableModalitiesV2 which will also skip theme dropdown selection.
      // SMC AAD users will also go to here, skip theme dropdown and go to modalityOptions stage to display tenant admin message.
      // contactSearchedQuery check is to differentiate the second userInfo call after filling out a query and click on get help button.
      // IsDeepLinkQuery check is to differentiate between userinfo calls for deep link scenario, IsDeepLinkQuery is true for first call and then set to false for second call via alchemy success action
    } else if (!!sharedState.contactSearchedQuery && !sharedState.isDeepLinkQuery && !!modalityState.modalityResponse) {
      const page: ServiceRequestPage = {
        isBackButtonPresent: true,
        serviceRequestPageType: ServiceRequestPageType.ModalityOptions,
      };
      dispatchInitializeToPage(page);
    }
  } else {
    getHelpLogger.logSeriveRequestManagerLoaded(pages.length, ServiceRequestPageType[currentPageType]);
                
                
                                                                                                                  
      
             
  }

  let mainPage: JSX.Element = null;

  if (!isRenderingModalitySection) {
    mainPage = null;
  } else if (isCollapsedModalities) {
    mainPage = <PageHeader theme={props.theme} />;
  } else {
    switch (currentPageType) {
      case ServiceRequestPageType.SignInSection:
        mainPage = <SignInSection {...props} />;
        break;
      case ServiceRequestPageType.AlchemyThemeRouting:
        mainPage = <AlchemyThemeRoutingContainer />;
        break;
      case ServiceRequestPageType.ModalityOptions:
        mainPage = <ModalityOptionsContainer />;
        break;
      case ServiceRequestPageType.RequestCreation:
        mainPage = <RequestCreationContainer />;
        break;
      case ServiceRequestPageType.RequestConfirmation:
        mainPage = <RequestConfirmationContainer />;
        break;
      default:
        mainPage = null;
    }
  }

  return (
    <div className={mergeStyles(contactUsClassNames.row, contactUsClassNames.noGutter)}>
      <div className={mergeStyles('col-lg-10', contactUsClassNames.col, contactUsClassNames.articleSection)}>
        <div className={contactUsClassNames.panelGroup}>
          <div className={mergeStyles('panel-default', contactUsClassNames.panel)}>{mainPage}</div>
        </div>
      </div>
    </div>
  );
};
